import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

const RecordTestPageNew = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams();

  // API基础URL - 直接指向backend-workers
  const API_BASE_URL = process.env.REACT_APP_WORKERS_API_URL || 'http://localhost:3002';

  // 健康状态
  const [healthStatus, setHealthStatus] = useState('checking');
  const [healthError, setHealthError] = useState(null);

  // 简化的状态
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [segments, setSegments] = useState([]);
  const [error, setError] = useState('');
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [uploadInterval] = useState(5); // 固定5秒间隔
  const [isHistoricalSession, setIsHistoricalSession] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionResult, setTranscriptionResult] = useState(null);

  // 录音器相关
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const splitTimerRef = useRef(null);
  const segmentIndexRef = useRef(0);
  const chunksRef = useRef([]);
  const audioIdRef = useRef(null);
  const isRecordingRef = useRef(false);
  const audioRef = useRef(null);
  const segmentStartTimeRef = useRef(null);
  const recordingStartTimeRef = useRef(null);

  // 本地音频缓存 - 用于播放
  const audioSegmentsRef = useRef(new Map()); // segmentIndex -> {audioBlob, audioUrl}

  // 任务队列管理
  const taskQueueRef = useRef([]);
  const isProcessingQueueRef = useRef(false);
  const failedTasksRef = useRef({});
  const retryTimersRef = useRef({});

  // 格式化时间显示
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // 复制录音会话ID到剪贴板
  const copySessionId = async () => {
    const audioId = audioIdRef.current || sessionId;
    if (!audioId) return;

    try {
      await navigator.clipboard.writeText(audioId);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('复制失败:', err);
    }
  };

  // 片段状态枚举
  const SegmentStatus = {
    PENDING: 'pending',
    UPLOADING: 'uploading',
    UPLOADED: 'uploaded',
    TRANSCRIBING: 'transcribing',
    COMPLETED: 'completed',
    FAILED: 'failed',
    RETRYING: 'retrying'
  };

  // 生成录音会话ID
  const generateAudioId = () => {
    return uuidv4();
  };

  // 新的上传和转写函数
  const uploadAndTranscribe = async (audioBlob, audioId, segmentIndex, duration) => {
    console.log(`🚀 开始上传和转写片段 ${segmentIndex}`);

    const formData = new FormData();
    formData.append('segment', audioBlob, `segment_${segmentIndex}.webm`);
    formData.append('audio_id', audioId);
    formData.append('segment_index', segmentIndex.toString());
    formData.append('timestamp', new Date().toISOString());

    const response = await fetch(`${API_BASE_URL}/api/upload_and_transcribe`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `上传失败: ${response.status}`);
    }

    const result = await response.json();

    // 处理新的返回格式
    if (result.success) {
      console.log(`✅ 片段 ${segmentIndex} 处理成功`);
      console.log(`📤 上传状态: ${result.segment_uploaded ? '已上传' : '未上传'}`);
      console.log(`📝 转写状态: ${result.transcription_completed ? '已完成' : '仅上传'}`);

      if (result.transcription_text) {
        console.log(`📄 转写内容: "${result.transcription_text.substring(0, 100)}${result.transcription_text.length > 100 ? '...' : ''}"`);
      }
    } else {
      console.log(`❌ 片段 ${segmentIndex} 处理失败: ${result.error}`);

      // 处理特殊的错误情况
      if (result.transcription_successful && result.transcription_text) {
        console.log(`💡 特殊情况：转写成功但JSON保存失败`);
        console.log(`📄 转写内容仍可用: "${result.transcription_text.substring(0, 100)}${result.transcription_text.length > 100 ? '...' : ''}"`);
      }

      if (result.qwen_error) {
        console.log(`🤖 千问错误详情: ${result.qwen_error}`);
      }

      if (result.qwen_response) {
        console.log(`🔍 千问完整响应:`, result.qwen_response);
      }
    }

    return result;
  };

  // 查询单个segment状态
  const querySegmentStatus = async (audioId, segmentIndex) => {
    console.log(`🔍 [前端] 开始查询segment状态: audio_id=${audioId}, segment_index=${segmentIndex}`);

    const url = `${API_BASE_URL}/api/segment/${audioId}/${segmentIndex}/status`;
    console.log(`🔍 [前端] 请求URL: ${url}`);

    try {
      const response = await fetch(url);
      console.log(`🔍 [前端] 响应状态: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ [前端] segment状态查询失败: ${response.status} - ${errorText}`);
        throw new Error(`查询失败: ${response.status}`);
      }

      const result = await response.json();
      console.log(`✅ [前端] segment状态查询成功:`, result);
      return result;
    } catch (error) {
      console.error(`❌ [前端] segment状态查询异常:`, error);
      throw error;
    }
  };

  // 查询整个audio会话状态
  const queryAudioStatus = async (audioId) => {
    console.log(`🔍 [前端] 开始查询audio状态: audio_id=${audioId}`);

    const url = `${API_BASE_URL}/api/audio/${audioId}/status`;
    console.log(`🔍 [前端] 请求URL: ${url}`);

    try {
      const response = await fetch(url);
      console.log(`🔍 [前端] audio状态响应: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ [前端] audio状态查询失败: ${response.status} - ${errorText}`);
        throw new Error(`查询失败: ${response.status}`);
      }

      const result = await response.json();
      console.log(`✅ [前端] audio状态查询成功:`, result);
      return result;
    } catch (error) {
      console.error(`❌ [前端] audio状态查询异常:`, error);
      throw error;
    }
  };

  // 检查后端健康状态
  const checkHealthStatus = async () => {
    try {
      setHealthStatus('checking');
      setHealthError(null);

      const response = await fetch(`${API_BASE_URL}/api/health`);

      if (response.ok) {
        const healthData = await response.json();
        setHealthStatus('healthy');
        console.log('🟢 后端健康状态:', healthData);
      } else {
        setHealthStatus('unhealthy');
        setHealthError(`HTTP ${response.status}`);
        console.warn('🟡 后端响应异常:', response.status);
      }
    } catch (error) {
      setHealthStatus('error');
      setHealthError(error.message);
      console.error('🔴 后端健康检查失败:', error);
    }
  };

  // 任务类
  class SegmentTask {
    constructor(audioBlob, audioId, segmentIndex, duration) {
      this.audioBlob = audioBlob;
      this.audioId = audioId;
      this.segmentIndex = segmentIndex;
      this.duration = duration;
      this.uploadAttempted = false; // 标记是否已经尝试过上传
      this.retryCount = 0;
      this.maxRetries = 1; // 每个segment最多只能重试一次（总共最多处理2次）
      this.lastError = null;
      this.nextRetryTime = null;
      this.status = SegmentStatus.PENDING;
    }

    async execute() {
      try {
        // 第一次尝试：上传并转写
        if (!this.uploadAttempted) {
          this.status = SegmentStatus.UPLOADING;
          this.updateUI();

          const result = await uploadAndTranscribe(
            this.audioBlob,
            this.audioId,
            this.segmentIndex,
            this.duration
          );

          this.uploadAttempted = true;

          // 清理失败记录
          if (failedTasksRef.current[this.segmentIndex]) {
            delete failedTasksRef.current[this.segmentIndex];
          }

          // 无论API返回什么结果，都必须进行状态查询以确保安全性
          console.log(`🔍 片段 ${this.segmentIndex} upload_and_transcribe 完成，进行状态查询确认`);

          try {
            const statusResult = await querySegmentStatus(this.audioId, this.segmentIndex);

            if (statusResult.success) {
              if (statusResult.transcription_file_exists) {
                // 状态查询确认转写完成
                this.status = SegmentStatus.COMPLETED;
                console.log(`✅ 片段 ${this.segmentIndex} 转写完成（状态查询确认）`);

                // 如果API也返回了转写内容，进行对比验证
                if (result.success && result.transcription_text) {
                  console.log(`📄 API返回的转写内容: "${result.transcription_text.substring(0, 100)}..."`);
                  console.log(`✅ API结果与状态查询一致，转写成功`);
                }
              } else if (statusResult.audio_file_exists) {
                // 状态查询确认仅上传成功，需要继续转写
                console.log(`📋 片段 ${this.segmentIndex} 上传成功但转写未完成（状态查询确认），开始转写`);

                try {
                  const transcribeResult = await this.transcribeOnly(statusResult.audio_file_url);

                  if (transcribeResult.success) {
                    // transcribe_only完成后再次查询状态确认
                    console.log(`🔍 片段 ${this.segmentIndex} transcribe_only 完成，再次状态查询`);

                    try {
                      const finalStatusResult = await querySegmentStatus(this.audioId, this.segmentIndex);

                      if (finalStatusResult.success && finalStatusResult.transcription_file_exists) {
                        this.status = SegmentStatus.COMPLETED;
                        console.log(`✅ 片段 ${this.segmentIndex} 后续转写完成（状态查询确认）`);
                      } else {
                        this.status = SegmentStatus.FAILED;
                        console.log(`❌ 片段 ${this.segmentIndex} 后续转写后状态查询失败`);
                      }
                    } catch (statusError) {
                      console.error(`❌ 片段 ${this.segmentIndex} 后续转写后状态查询异常:`, statusError);
                      this.status = SegmentStatus.FAILED;
                    }
                  } else {
                    console.error(`❌ 片段 ${this.segmentIndex} 后续转写失败: ${transcribeResult.error}`);
                    this.status = SegmentStatus.FAILED;
                  }
                } catch (transcribeError) {
                  console.error(`❌ 片段 ${this.segmentIndex} 后续转写异常:`, transcribeError);
                  this.status = SegmentStatus.FAILED;
                }
              } else {
                // 状态查询确认文件都不存在
                this.status = SegmentStatus.FAILED;
                console.log(`❌ 片段 ${this.segmentIndex} 处理失败，文件都不存在（状态查询确认）`);
              }
            } else {
              this.status = SegmentStatus.FAILED;
              console.log(`❌ 片段 ${this.segmentIndex} 状态查询失败`);
            }
          } catch (statusError) {
            console.error(`❌ 片段 ${this.segmentIndex} 状态查询异常:`, statusError);

            // 状态查询失败时，基于API返回结果进行判断
            if (result.success && result.transcription_completed) {
              this.status = SegmentStatus.COMPLETED;
              console.log(`⚠️ 状态查询失败，但API显示转写完成，标记为完成`);
            } else if (result.success && result.segment_uploaded) {
              this.status = SegmentStatus.UPLOADED;
              console.log(`⚠️ 状态查询失败，但API显示上传成功，标记为仅上传`);
            } else {
              this.status = SegmentStatus.FAILED;
              console.log(`❌ 状态查询失败，API也失败，标记为失败`);
            }
          }

          this.updateUI();
          return result;
        }

        // 第二次尝试（重试）：先查询状态，根据状态决定重试方式
        if (this.retryCount === 0) {
          this.status = SegmentStatus.UPLOADING;
          this.updateUI();

          console.log(`📋 片段 ${this.segmentIndex} 首次失败，查询状态决定重试方式`);

          const statusResult = await querySegmentStatus(this.audioId, this.segmentIndex);

          if (statusResult.success) {
            if (statusResult.transcription_file_exists) {
              // 转写已经完成
              this.status = SegmentStatus.COMPLETED;
              this.updateUI();
              console.log(`✅ 片段 ${this.segmentIndex} 转写已完成（状态查询发现）`);
              return statusResult;
            } else if (statusResult.audio_file_exists) {
              // 上传成功但转写失败，仅重新转写
              console.log(`📋 片段 ${this.segmentIndex} 上传成功但转写失败，仅重新转写`);

              const transcribeResult = await this.transcribeOnly(statusResult.audio_file_url);

              if (transcribeResult.success) {
                // transcribe_only完成后也要查询状态确认
                console.log(`🔍 片段 ${this.segmentIndex} transcribe_only 完成，查询最终状态`);

                try {
                  const finalStatusResult = await querySegmentStatus(this.audioId, this.segmentIndex);

                  if (finalStatusResult.success && finalStatusResult.transcription_file_exists) {
                    this.status = SegmentStatus.COMPLETED;
                    console.log(`✅ 片段 ${this.segmentIndex} 重新转写成功（状态查询确认）`);
                  } else {
                    this.status = SegmentStatus.FAILED;
                    console.log(`❌ 片段 ${this.segmentIndex} 重新转写后状态查询失败`);
                  }
                } catch (statusError) {
                  console.error(`❌ 片段 ${this.segmentIndex} 重新转写后状态查询异常:`, statusError);
                  this.status = SegmentStatus.COMPLETED; // 假设成功，因为API返回success
                }

                this.updateUI();
                return transcribeResult;
              } else {
                throw new Error(`重新转写失败: ${transcribeResult.error}`);
              }
            } else {
              // 文件都不存在，重新上传并转写
              console.log(`📋 片段 ${this.segmentIndex} 文件都不存在，重新上传并转写`);

              const result = await uploadAndTranscribe(
                this.audioBlob,
                this.audioId,
                this.segmentIndex,
                this.duration
              );

              // 重新上传并转写完成后也要查询状态确认
              console.log(`🔍 片段 ${this.segmentIndex} 重新上传并转写完成，查询最终状态`);

              try {
                const finalStatusResult = await querySegmentStatus(this.audioId, this.segmentIndex);

                if (finalStatusResult.success) {
                  if (finalStatusResult.transcription_file_exists) {
                    this.status = SegmentStatus.COMPLETED;
                    console.log(`✅ 片段 ${this.segmentIndex} 重新上传并转写成功（状态查询确认）`);
                  } else if (finalStatusResult.audio_file_exists) {
                    this.status = SegmentStatus.UPLOADED;
                    console.log(`📤 片段 ${this.segmentIndex} 重新上传成功但转写未完成（状态查询确认）`);
                  } else {
                    this.status = SegmentStatus.FAILED;
                    console.log(`❌ 片段 ${this.segmentIndex} 重新处理失败，文件都不存在（状态查询确认）`);
                  }
                } else {
                  this.status = SegmentStatus.FAILED;
                  console.log(`❌ 片段 ${this.segmentIndex} 重新处理后状态查询失败`);
                }
              } catch (statusError) {
                console.error(`❌ 片段 ${this.segmentIndex} 重新处理后状态查询异常:`, statusError);
                this.status = result.transcription_completed ? SegmentStatus.COMPLETED : SegmentStatus.FAILED;
              }

              this.updateUI();
              return result;
            }
          } else {
            throw new Error(`查询状态失败: ${statusResult.error}`);
          }
        }

      } catch (error) {
        // 即使失败，也要查询状态确认实际情况
        console.log(`🔍 片段 ${this.segmentIndex} upload_and_transcribe 失败，查询实际状态`);

        let statusResult = null;
        try {
          statusResult = await querySegmentStatus(this.audioId, this.segmentIndex);

          if (statusResult.success) {
            if (statusResult.transcription_file_exists) {
              this.status = SegmentStatus.COMPLETED;
              console.log(`✅ 片段 ${this.segmentIndex} 转写已完成（尽管API失败，状态查询确认成功）`);
            } else if (statusResult.audio_file_exists) {
              this.status = SegmentStatus.UPLOADED;
              console.log(`📤 片段 ${this.segmentIndex} 上传成功但转写未完成（尽管API失败，状态查询确认）`);
            } else {
              this.status = SegmentStatus.FAILED;
              console.log(`❌ 片段 ${this.segmentIndex} 确实失败，文件都不存在（状态查询确认）`);
            }
          } else {
            this.status = SegmentStatus.FAILED;
            console.log(`❌ 片段 ${this.segmentIndex} 状态查询也失败`);
          }
        } catch (statusError) {
          console.error(`❌ 片段 ${this.segmentIndex} 失败后状态查询异常:`, statusError);
        }

        // 只有在真正失败的情况下才调用onError
        // 如果状态查询显示文件已上传，就不要标记为失败
        if (this.status !== SegmentStatus.COMPLETED && this.status !== SegmentStatus.UPLOADED) {
          this.onError(error);
          throw error;
        } else if (this.status === SegmentStatus.UPLOADED && statusResult && statusResult.audio_file_url) {
          // 如果是uploaded状态，需要继续转写
          console.log(`📋 片段 ${this.segmentIndex} 失败后发现文件已上传，开始转写`);

          try {
            const transcribeResult = await this.transcribeOnly(statusResult.audio_file_url);

            if (transcribeResult.success) {
              // transcribe_only完成后再次查询状态确认
              console.log(`🔍 片段 ${this.segmentIndex} 转写完成，查询最终状态确认`);

              try {
                const finalStatusResult = await querySegmentStatus(this.audioId, this.segmentIndex);

                if (finalStatusResult.success && finalStatusResult.transcription_file_exists) {
                  this.status = SegmentStatus.COMPLETED;
                  console.log(`✅ 片段 ${this.segmentIndex} 转写完成（失败后状态查询+转写成功）`);
                  this.updateUI();
                  return transcribeResult;
                } else {
                  this.status = SegmentStatus.FAILED;
                  console.log(`❌ 片段 ${this.segmentIndex} 转写后状态查询失败`);
                }
              } catch (statusError) {
                console.error(`❌ 片段 ${this.segmentIndex} 转写后状态查询异常:`, statusError);
                this.status = SegmentStatus.COMPLETED; // 假设成功，因为API返回success
              }
            } else {
              console.error(`❌ 片段 ${this.segmentIndex} 转写失败: ${transcribeResult.error}`);
              this.status = SegmentStatus.FAILED;
              this.onError(error);
              throw error;
            }
          } catch (transcribeError) {
            console.error(`❌ 片段 ${this.segmentIndex} 转写异常:`, transcribeError);
            this.status = SegmentStatus.FAILED;
            this.onError(error);
            throw error;
          }
        }
      }
    }

    // 仅转写方法
    async transcribeOnly(audioFileUrl) {
      try {
        console.log(`🚀 开始仅转写片段 ${this.segmentIndex}`);
        const response = await fetch(`${API_BASE_URL}/api/transcribe_only`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            audio_id: this.audioId,
            segment_index: this.segmentIndex,
            audio_file_url: audioFileUrl
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `转写失败: ${response.status}`);
        }

        const result = await response.json();
        console.log(`📝 片段 ${this.segmentIndex} transcribe_only API返回:`, result);

        // 无论转写API返回什么结果，都必须进行状态查询确认
        console.log(`🔍 片段 ${this.segmentIndex} transcribe_only 完成，进行状态查询确认`);

        try {
          const statusResult = await querySegmentStatus(this.audioId, this.segmentIndex);

          if (statusResult.success && statusResult.transcription_file_exists) {
            console.log(`✅ 片段 ${this.segmentIndex} 转写完成（状态查询确认）`);

            // 如果API也返回了转写内容，进行验证
            if (result.success && result.transcription_text) {
              console.log(`📄 API返回的转写内容: "${result.transcription_text.substring(0, 100)}..."`);
              console.log(`✅ 转写API结果与状态查询一致`);
            }

            return {
              ...result,
              verified_by_status_query: true,
              final_status: 'completed'
            };
          } else {
            console.log(`❌ 片段 ${this.segmentIndex} 转写后状态查询失败，转写可能未成功`);
            return {
              ...result,
              verified_by_status_query: true,
              final_status: 'failed'
            };
          }
        } catch (statusError) {
          console.error(`❌ 片段 ${this.segmentIndex} 转写后状态查询异常:`, statusError);
          console.log(`⚠️ 状态查询失败，基于API返回结果判断`);
          return {
            ...result,
            verified_by_status_query: false,
            status_query_error: statusError.message
          };
        }

      } catch (error) {
        console.error(`❌ 片段 ${this.segmentIndex} 仅转写失败:`, error);

        // 即使转写API失败，也要查询状态确认实际情况
        console.log(`🔍 片段 ${this.segmentIndex} 转写失败，查询实际状态`);

        try {
          const statusResult = await querySegmentStatus(this.audioId, this.segmentIndex);

          if (statusResult.success && statusResult.transcription_file_exists) {
            console.log(`💡 片段 ${this.segmentIndex} 尽管API失败，但状态查询显示转写已完成`);
            return {
              success: true,
              transcription_successful: true,
              api_failed: true,
              error: error.message,
              final_status: 'completed'
            };
          } else if (statusResult.success && statusResult.audio_file_exists) {
            console.log(`📤 片段 ${this.segmentIndex} 尽管API失败，但状态查询显示文件已上传`);
            return {
              success: false,
              transcription_successful: false,
              api_failed: true,
              error: error.message,
              final_status: 'uploaded'
            };
          } else {
            console.log(`❌ 片段 ${this.segmentIndex} API失败，状态查询也确认失败`);
            throw error;
          }
        } catch (statusError) {
          console.error(`❌ 片段 ${this.segmentIndex} 失败后状态查询异常:`, statusError);
          throw error;
        }
      }
    }

    onError(error) {
      console.error(`❌ 片段 ${this.segmentIndex} 处理失败:`, error);

      this.lastError = error.message;
      this.status = SegmentStatus.FAILED;

      // 重试逻辑：总共最多处理2次（首次1次+重试1次）
      if (this.retryCount === 0) {
        // 第一次失败，安排重试
        this.retryCount++;
        console.log(`🔄 片段 ${this.segmentIndex} 首次失败，3秒后重试 (第${this.retryCount}/${this.maxRetries + 1}次处理)`);

        const retryDelay = 3000;
        this.nextRetryTime = Date.now() + retryDelay;

        // 安排重试
        scheduleRetry(this);
      } else {
        // 第二次失败，停止重试
        console.error(`💀 片段 ${this.segmentIndex} 处理失败 (总共${this.retryCount + 1}次)，达到最大次数，最终失败`);
        this.status = SegmentStatus.FAILED;

        // 清理重试定时器，确保不会再继续重试
        if (retryTimersRef.current[this.segmentIndex]) {
          clearTimeout(retryTimersRef.current[this.segmentIndex]);
          delete retryTimersRef.current[this.segmentIndex];
        }
      }

      this.updateUI();
    }

    updateUI() {
      setSegments(prev => {
        const exists = prev.some(seg => seg.index === this.segmentIndex);
        if (!exists) {
          // 检查是否有本地音频缓存
          const localAudioData = audioSegmentsRef.current.get(this.segmentIndex);

          return [...prev, {
            index: this.segmentIndex,
            fileName: `segment_${this.segmentIndex}.webm`,
            size: this.audioBlob.size,
            duration: this.duration,
            status: this.status,
            retryCount: this.retryCount,
            maxRetries: this.maxRetries,
            error: this.lastError,
            nextRetryTime: this.nextRetryTime,
            uploadAttempted: this.uploadAttempted,
            localAudioUrl: localAudioData?.audioUrl || null,
            hasLocalAudio: !!localAudioData
          }];
        } else {
          return prev.map(seg =>
            seg.index === this.segmentIndex
              ? {
                  ...seg,
                  status: this.status,
                  retryCount: this.retryCount,
                  error: this.lastError,
                  nextRetryTime: this.nextRetryTime,
                  uploadAttempted: this.uploadAttempted
                }
              : seg
          );
        }
      });
    }
  }

  // 安排重试
  const scheduleRetry = (task) => {
    const retryDelay = task.nextRetryTime - Date.now();

    console.log(`⏰ 安排片段 ${task.segmentIndex} 在 ${retryDelay/1000} 秒后重试`);

    const timerId = setTimeout(async () => {
      // 确保只重试一次
      if (task.retryCount === 1) {
        task.status = SegmentStatus.RETRYING;
        task.updateUI();

        try {
          await task.execute();
        } catch (err) {
          console.error(`❌ 片段 ${task.segmentIndex} 重试失败:`, err);
        }
      } else {
        console.log(`⏭️ 片段 ${task.segmentIndex} 处理次数已达到上限 (最多2次)，停止重试`);
      }

      // 清理定时器记录
      delete retryTimersRef.current[task.segmentIndex];
    }, retryDelay);

    retryTimersRef.current[task.segmentIndex] = timerId;
  };

  // 处理任务队列
  const processQueue = async () => {
    if (isProcessingQueueRef.current || taskQueueRef.current.length === 0) {
      return;
    }

    isProcessingQueueRef.current = true;
    console.log(`🔄 开始处理任务队列，剩余 ${taskQueueRef.current.length} 个任务`);

    while (taskQueueRef.current.length > 0) {
      const task = taskQueueRef.current.shift();

      try {
        await task.execute();
      } catch (error) {
        console.error(`任务执行失败:`, error);
        // 任务内部已经处理了重试逻辑
      }
    }

    isProcessingQueueRef.current = false;
    console.log('✅ 任务队列处理完成');
  };

  // 添加任务到队列
  const addTaskToQueue = (audioBlob, audioId, segmentIndex, duration) => {
    const task = new SegmentTask(audioBlob, audioId, segmentIndex, duration);
    taskQueueRef.current.push(task);

    // 立即尝试处理队列
    processQueue();
  };

  // 加载历史会话数据
  const loadHistoricalSession = async (audioId) => {
    try {
      console.log('🔍 加载历史会话数据:', audioId);

      const sessionData = await queryAudioStatus(audioId);
      console.log('📋 历史会话信息:', sessionData);

      // 转换历史片段数据格式
      const formattedSegments = sessionData.segments.map(segment => ({
        index: segment.segment_index,
        fileName: `segment_${segment.segment_index}.webm`,
        size: 0, // 历史数据中可能没有size信息
        duration: 5, // 默认5秒
        status: segment.status === 'completed' ? SegmentStatus.COMPLETED :
                segment.status === 'uploaded' ? SegmentStatus.UPLOADED :
                SegmentStatus.FAILED,
        transcription: segment.transcription_data
      }));

      setSegments(formattedSegments);
      setIsHistoricalSession(true);
      setError('');

    } catch (err) {
      console.error('❌ 加载历史会话失败:', err);
      setError('加载历史会话失败: ' + err.message);
    }
  };

  // URL参数解析和会话加载
  useEffect(() => {
    if (sessionId) {
      console.log('🔗 检测到URL中的会话ID:', sessionId);
      audioIdRef.current = sessionId;
      loadHistoricalSession(sessionId);
    }
  }, [sessionId]);

  // 健康状态检查 - 只在页面启动时检查一次
  useEffect(() => {
    // 页面启动时检查一次后端健康状态
    checkHealthStatus();
  }, [API_BASE_URL]);

  // 开始录音
  const startRecording = async () => {
    try {
      setError('');
      setSegments([]);
      setRecordingTime(0);
      setIsHistoricalSession(false);
      segmentIndexRef.current = 0;
      chunksRef.current = [];
      audioSegmentsRef.current = new Map(); // 清理本地音频缓存
      taskQueueRef.current = [];

      // 清除URL参数，开始新的录音会话
      navigate('/record-test', { replace: true });

      setIsStarting(true);
      console.log('🚀 开始初始化录音...');

      const newAudioId = generateAudioId();
      audioIdRef.current = newAudioId;

      // 获取麦克风权限
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });

      console.log('🎤 麦克风权限获取成功');
      streamRef.current = stream;

      // 创建MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      mediaRecorderRef.current = mediaRecorder;

      // 录音数据处理 - 基于audio-segment-recorder.html的逻辑
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0 && isRecordingRef.current) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        createAudioSegment();
      };

      // 立即开始录音
      isRecordingRef.current = true;
      const now = Date.now();
      recordingStartTimeRef.current = now;
      segmentStartTimeRef.current = now;

      mediaRecorder.start();
      setIsRecording(true);
      setIsStarting(false);

      // 启动定时器
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      // 启动切分定时器 - 每5秒自动切分
      startSplitTimer();

      console.log('✅ 录音启动成功');

    } catch (err) {
      console.error('录音启动失败:', err);
      setError('无法访问麦克风，请检查权限设置');
      setIsStarting(false);
    }
  };

  // 启动切分定时器
  const startSplitTimer = () => {
    splitTimerRef.current = setInterval(() => {
      if (isRecordingRef.current && mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        console.log('🔪 执行音频切分');
        // 停止当前录制并创建片段
        mediaRecorderRef.current.stop();

        // 立即开始新的录制
        setTimeout(() => {
          if (isRecordingRef.current && mediaRecorderRef.current) {
            mediaRecorderRef.current.start();
            segmentStartTimeRef.current = Date.now();
            console.log('🎙️ 开始新的录音段');
          }
        }, 100); // 短暂延迟确保stop完成
      }
    }, uploadInterval * 1000); // 每5秒切分一次
  };

  // 创建音频片段
  const createAudioSegment = () => {
    if (chunksRef.current.length === 0) {
      console.log('⚠️ 没有音频数据，跳过片段创建');
      return;
    }

    console.log(`🎵 创建音频片段 ${segmentIndexRef.current}`);

    const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm;codecs=opus' });
    const audioUrl = URL.createObjectURL(audioBlob);
    const duration = (Date.now() - segmentStartTimeRef.current) / 1000;

    // 保存到本地缓存
    const segmentData = {
      audioBlob,
      audioUrl,
      segmentIndex: segmentIndexRef.current,
      duration,
      timestamp: new Date().toISOString()
    };

    audioSegmentsRef.current.set(segmentIndexRef.current, segmentData);

    // 添加到上传队列
    addTaskToQueue(audioBlob, audioIdRef.current, segmentIndexRef.current, duration);

    // 清空chunks准备下一段
    chunksRef.current = [];
    segmentIndexRef.current++;
  };

  // 暂停录音
  const pauseRecording = async () => {
    console.log('⏸️ 暂停录音...');

    // 停止切分定时器
    if (splitTimerRef.current) {
      clearInterval(splitTimerRef.current);
      splitTimerRef.current = null;
    }

    // 停止录音器，这会触发createAudioSegment
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setIsPaused(true);
    console.log('✅ 录音已暂停');
  };

  // 继续录音
  const resumeRecording = () => {
    console.log('▶️ 继续录音...');

    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
    } else if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'inactive') {
      // 重新开始录音
      mediaRecorderRef.current.start();
      segmentStartTimeRef.current = Date.now();
    }

    // 重新启动定时器
    timerRef.current = setInterval(() => {
      setRecordingTime(prev => prev + 1);
    }, 1000);

    // 重新启动切分定时器
    startSplitTimer();

    setIsPaused(false);
    console.log('✅ 录音已继续');
  };

  // 取消录音
  const cancelRecording = () => {
    console.log('❌ 开始取消录音...');

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (splitTimerRef.current) {
      clearInterval(splitTimerRef.current);
      splitTimerRef.current = null;
    }

    // 清理所有定时器和任务
    Object.values(retryTimersRef.current).forEach(timerId => {
      clearTimeout(timerId);
    });
    retryTimersRef.current = {};
    taskQueueRef.current = [];

    // 清理本地音频缓存
    audioSegmentsRef.current.forEach(segmentData => {
      URL.revokeObjectURL(segmentData.audioUrl);
    });
    audioSegmentsRef.current = new Map();

    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }

    isRecordingRef.current = false;
    setIsRecording(false);
    setIsPaused(false);
    setIsStarting(false);
    setRecordingTime(0);
    setSegments([]);
    setError('');
    setIsPlaying(false);
    setIsAudioLoading(false);

    audioIdRef.current = null;
    segmentIndexRef.current = 0;
    chunksRef.current = [];
    mediaRecorderRef.current = null;
    segmentStartTimeRef.current = null;
    recordingStartTimeRef.current = null;

    console.log('✅ 录音已取消');
  };

  // 停止录音
  const stopRecording = async () => {
    console.log('🛑 开始停止录音流程...');
    setIsStopping(true);

    const finalRecordingTime = recordingTime;
    const finalAudioId = audioIdRef.current;
    const finalSegmentIndex = segmentIndexRef.current;

    console.log('📊 录音结束状态:');
    console.log('  - Audio ID:', finalAudioId);
    console.log('  - 录音时长:', finalRecordingTime, '秒');
    console.log('  - 当前片段索引:', finalSegmentIndex);

    // 停止切分定时器
    if (splitTimerRef.current) {
      clearInterval(splitTimerRef.current);
      splitTimerRef.current = null;
    }

    // 停止录音器，这会触发最后一个片段的创建
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    isRecordingRef.current = false;
    setIsRecording(false);
    setIsPaused(false);

    // 等待所有任务完成
    console.log('⏳ 等待所有任务完成...');
    await waitForAllTasksComplete();

    // 跳转到历史会话页面
    console.log('🔗 跳转到历史会话页面:', finalAudioId);
    navigate(`/record-test/${finalAudioId}`, { replace: true });
    setIsStopping(false);
  };

  // 等待所有任务完成
  const waitForAllTasksComplete = async () => {
    let attempts = 0;
    const maxAttempts = 30; // 最多等待30秒

    while (attempts < maxAttempts) {
      const queueEmpty = taskQueueRef.current.length === 0;
      const noRetries = Object.keys(retryTimersRef.current).length === 0;

      if (queueEmpty && noRetries) {
        console.log('✅ 所有任务已完成');
        return;
      }

      await new Promise(resolve => setTimeout(resolve, 1000));
      attempts++;
    }

    console.warn('⚠️ 强制完成等待，可能还有未完成的任务');
  };

  // 获取片段状态图标
  const getSegmentStatusIcon = (segment) => {
    switch (segment.status) {
      case SegmentStatus.COMPLETED:
        return '✅';
      case SegmentStatus.UPLOADED:
        return '⬆️';
      case SegmentStatus.UPLOADING:
      case SegmentStatus.TRANSCRIBING:
        return '🔄';
      case SegmentStatus.RETRYING:
        return '🔄';
      case SegmentStatus.FAILED:
        return '❌';
      default:
        return '⏳';
    }
  };

  // 获取片段状态文本
  const getSegmentStatusText = (segment) => {
    switch (segment.status) {
      case SegmentStatus.COMPLETED:
        return '转写完成';
      case SegmentStatus.UPLOADED:
        return '已上传';
      case SegmentStatus.UPLOADING:
        return segment.uploadAttempted ? '查询状态中...' : '上传中...';
      case SegmentStatus.TRANSCRIBING:
        return '转写中...';
      case SegmentStatus.RETRYING:
        return `重试中 (第${segment.retryCount}/${segment.maxRetries}次)`;
      case SegmentStatus.FAILED:
        if (segment.uploadAttempted) {
          return '上传失败，请检查网络';
        }
        if (segment.retryCount >= segment.maxRetries) {
          return `重试失败: ${segment.error || '已达最大重试次数'}`;
        }
        if (segment.nextRetryTime) {
          const waitTime = Math.ceil((segment.nextRetryTime - Date.now()) / 1000);
          return waitTime > 0 ? `${waitTime}秒后重试` : '准备重试';
        }
        return `失败: ${segment.error || '未知错误'}`;
      default:
        return '等待处理';
    }
  };

  // 播放本地音频
  const playLocalAudio = (segmentIndex) => {
    const audioData = audioSegmentsRef.current.get(segmentIndex);
    if (audioData && audioData.audioUrl) {
      const audio = new Audio(audioData.audioUrl);
      audio.play().catch(err => {
        console.error(`播放片段 ${segmentIndex} 本地音频失败:`, err);
      });
    } else {
      console.warn(`片段 ${segmentIndex} 没有本地音频数据`);
    }
  };

  // 播放远程音频
  const playRemoteAudio = async (audioId, segmentIndex) => {
    try {
      // 构建远程音频URL，这里假设后端提供了音频访问接口
      const remoteAudioUrl = `${API_BASE_URL}/api/audio/${audioId}/segment/${segmentIndex}/play`;

      const audio = new Audio(remoteAudioUrl);
      audio.play().catch(err => {
        console.error(`播放片段 ${segmentIndex} 远程音频失败:`, err);
        alert(`播放远程音频失败: ${err.message}`);
      });
    } catch (error) {
      console.error(`获取片段 ${segmentIndex} 远程音频URL失败:`, error);
      alert(`获取远程音频失败: ${error.message}`);
    }
  };

  // 下载音频片段
  const downloadSegment = (segmentIndex, isLocal = true) => {
    let url, filename;

    if (isLocal) {
      const audioData = audioSegmentsRef.current.get(segmentIndex);
      if (!audioData || !audioData.audioUrl) {
        console.warn(`片段 ${segmentIndex} 没有本地音频数据`);
        return;
      }
      url = audioData.audioUrl;
      filename = `segment_${segmentIndex}_local.webm`;
    } else {
      // 下载远程音频
      const audioId = audioIdRef.current || sessionId;
      url = `${API_BASE_URL}/api/audio/${audioId}/segment/${segmentIndex}/download`;
      filename = `segment_${segmentIndex}_remote.webm`;
    }

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // 清理资源
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (splitTimerRef.current) {
        clearInterval(splitTimerRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }

      // 清理本地音频缓存
      audioSegmentsRef.current.forEach(segmentData => {
        URL.revokeObjectURL(segmentData.audioUrl);
      });
      audioSegmentsRef.current = new Map();

      // 清理定时器
      Object.values(retryTimersRef.current).forEach(timerId => {
        clearTimeout(timerId);
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-7xl">
        {/* 标题 */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">
            {isHistoricalSession ? '历史录音查看' : '录音测试 (新版)'}
          </h1>
          <p className="text-gray-400">
            {isHistoricalSession ? '查看历史录音会话' : '基于R2存储的音频分段录制系统'}
          </p>
          {isHistoricalSession && (
            <div className="mt-2">
              <button
                onClick={() => {
                  navigate('/record-test', { replace: true });
                  setIsHistoricalSession(false);
                  setSegments([]);
                  setAudioUrl(null);
                }}
                className="px-4 py-2 bg-blue-600 rounded-full text-white text-sm hover:bg-blue-700 transition-colors"
              >
                🎤 开始新录音
              </button>
            </div>
          )}
          <div className="flex items-center justify-center mt-2 space-x-4">
            <p className="text-gray-500 text-xs">API: {API_BASE_URL}</p>
            <div className="flex items-center space-x-2">
              {/* 健康状态指示器 */}
              {healthStatus === 'checking' && (
                <>
                  <div className="animate-spin rounded-full h-2 w-2 border border-yellow-400 border-t-transparent"></div>
                  <span className="text-yellow-400 text-xs">检查中</span>
                </>
              )}
              {healthStatus === 'healthy' && (
                <>
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span className="text-green-400 text-xs">正常</span>
                </>
              )}
              {healthStatus === 'unhealthy' && (
                <>
                  <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                  <span className="text-yellow-400 text-xs">异常</span>
                </>
              )}
              {healthStatus === 'error' && (
                <>
                  <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                  <span className="text-red-400 text-xs">离线</span>
                </>
              )}

              {/* 手动刷新按钮 */}
              <button
                onClick={checkHealthStatus}
                className="text-gray-400 hover:text-white transition-colors p-0.5 rounded hover:bg-gray-700"
                title="刷新健康状态"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
          </div>

          {/* 健康状态详情 */}
          {(healthStatus === 'unhealthy' || healthStatus === 'error') && (
            <div className={`mt-2 p-2 rounded text-xs ${
              healthStatus === 'unhealthy' ? 'bg-yellow-900 bg-opacity-30 text-yellow-300' :
              'bg-red-900 bg-opacity-30 text-red-300'
            }`}>
              <p>后端服务状态异常，可能影响录音和转写功能。</p>
              <p>错误信息: {healthError}</p>
              <p>请检查backend-workers服务是否正常运行。</p>
            </div>
          )}
        </div>

        {/* 错误提示 */}
        {error && (
          <div className="w-full max-w-4xl mx-auto mb-6 p-4 bg-red-900 bg-opacity-50 border border-red-600 rounded-lg">
            <div className="text-red-200 text-center text-sm">{error}</div>
          </div>
        )}

        {/* 主要内容 */}
        <div className="max-w-4xl mx-auto space-y-6">

          {/* 录音控制 */}
          {!isHistoricalSession && (
            <div className="bg-gray-900 rounded-lg p-6 text-center">
              {!isRecording && !isStarting && !isStopping ? (
                <button
                  onClick={startRecording}
                  className="px-8 py-4 bg-green-600 rounded-full text-white font-medium hover:bg-green-700 transition-colors"
                >
                  🎤 开始录音
                </button>
              ) : isStarting ? (
                <div className="space-y-4">
                  <div className="text-2xl font-mono">启动中...</div>
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
                  </div>
                  <div className="text-sm text-gray-400">正在初始化录音功能</div>
                </div>
              ) : isStopping ? (
                <div className="space-y-4">
                  <div className="text-2xl font-mono">停止中...</div>
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
                  </div>
                  <div className="text-sm text-gray-400">正在等待所有任务完成</div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-2xl font-mono">{formatTime(recordingTime)}</div>
                  <div className="flex justify-center space-x-4">
                    {!isPaused ? (
                      <>
                        <button
                          onClick={cancelRecording}
                          className="px-6 py-3 bg-gray-600 rounded-full text-white font-medium hover:bg-gray-700 transition-colors"
                        >
                          ❌ 取消
                        </button>
                        <button
                          onClick={pauseRecording}
                          className="px-6 py-3 bg-yellow-600 rounded-full text-white font-medium hover:bg-yellow-700 transition-colors"
                        >
                          ⏸️ 暂停
                        </button>
                        <button
                          onClick={stopRecording}
                          className="px-6 py-3 bg-red-600 rounded-full text-white font-medium hover:bg-red-700 transition-colors"
                        >
                          ⏹️ 停止录音
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={cancelRecording}
                          className="px-6 py-3 bg-gray-600 rounded-full text-white font-medium hover:bg-gray-700 transition-colors"
                        >
                          ❌ 取消
                        </button>
                        <button
                          onClick={resumeRecording}
                          className="px-6 py-3 bg-blue-600 rounded-full text-white font-medium hover:bg-blue-700 transition-colors"
                        >
                          ▶️ 继续
                        </button>
                        <button
                          onClick={stopRecording}
                          className="px-6 py-3 bg-red-600 rounded-full text-white font-medium hover:bg-red-700 transition-colors"
                        >
                          ⏹️ 停止录音
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 录音信息 */}
          {(isRecording || isHistoricalSession) && (
            <div className="bg-gray-900 rounded-lg p-6">
              <h2 className="text-lg font-bold mb-4">
                {isHistoricalSession ? '会话详情' : '录音信息'}
              </h2>
              <div className="grid grid-cols-1 gap-4 text-sm border border-gray-700 rounded-lg p-4 bg-gray-800">
                <div>
                  <div className="flex items-center justify-between">
                    <div className="text-gray-400">录音会话ID</div>
                    <button
                      onClick={copySessionId}
                      className="text-gray-400 hover:text-white transition-colors p-1 rounded hover:bg-gray-700"
                      title="复制录音会话ID"
                    >
                      {copySuccess ? (
                        <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <div className="font-mono text-xs break-all mt-1">
                    {audioIdRef.current || sessionId}
                  </div>
                  {copySuccess && (
                    <div className="text-green-400 text-xs mt-1">
                      ✓ 已复制到剪贴板
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-gray-400">录音时长</div>
                  <div className="font-mono">
                    {isRecording ? formatTime(recordingTime) : '历史会话'}
                  </div>
                </div>
                <div>
                  <div className="text-gray-400">任务队列状态</div>
                  <div className="font-mono">
                    队列: {taskQueueRef.current?.length || 0} | 重试: {Object.keys(retryTimersRef.current).length}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 录音片段列表 */}
          {segments.length > 0 && (
            <div className="bg-gray-900 rounded-lg p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
                <h2 className="text-lg font-bold">
                  {isHistoricalSession ? '历史片段' : '录音片段'}
                </h2>
                <div className="text-sm text-gray-400">
                  共 {segments.length} 个片段
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {segments.map((segment, index) => (
                  <div key={index} className={`bg-gray-800 rounded p-3 flex justify-between items-center ${
                    segment.status === SegmentStatus.UPLOADING || segment.status === SegmentStatus.TRANSCRIBING || segment.status === SegmentStatus.RETRYING ? 'border border-blue-500' :
                    segment.status === SegmentStatus.FAILED ? 'border border-red-500' :
                    segment.status === SegmentStatus.COMPLETED ? 'border border-green-500' : ''
                  }`}>
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="flex-shrink-0">
                        <span className="text-lg">
                          {getSegmentStatusIcon(segment)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{segment.fileName}</div>
                        <div className="text-xs text-gray-400">
                          {segment.duration}秒 | {segment.size ? `${(segment.size / 1024).toFixed(1)}KB` : 'N/A'}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {getSegmentStatusText(segment)}
                        </div>
                        {segment.transcription && (
                          <div className="text-xs text-green-400 mt-1 max-w-xs truncate">
                            "{segment.transcription.text}"
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 播放和下载按钮 */}
                    <div className="flex flex-col space-y-1 ml-2">
                      <div className="flex space-x-1">
                        {/* 本地播放按钮 */}
                        {segment.hasLocalAudio && (
                          <button
                            onClick={() => playLocalAudio(segment.index)}
                            className="p-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                            title="播放本地音频"
                          >
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M5 4v12l10-6z"/>
                            </svg>
                          </button>
                        )}

                        {/* 远程播放按钮 */}
                        <button
                          onClick={() => playRemoteAudio(audioIdRef.current || sessionId, segment.index)}
                          className="p-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                          title="播放远程音频"
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M5 4v12l10-6z"/>
                          </svg>
                        </button>

                        {/* 下载按钮 */}
                        <button
                          onClick={() => downloadSegment(segment.index, segment.hasLocalAudio)}
                          className="p-1 bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors"
                          title="下载音频"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </button>
                      </div>

                      {/* 按钮提示 */}
                      <div className="text-xs text-gray-500 text-center">
                        {segment.hasLocalAudio ? '本地 | 远程' : '远程'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 设置区域 */}
          {!isHistoricalSession && (
            <div className="bg-gray-900 rounded-lg p-4 text-sm text-gray-400">
              <div className="space-y-1">
                <p>• 点击"开始录音"开始录制</p>
                <p>• 每{uploadInterval}秒自动生成一个音频片段并上传转写</p>
                <p>• 点击"暂停"完成当前片段并暂停录音</p>
                <p>• 点击"继续"恢复录制</p>
                <p>• 点击"停止录音"结束录制并等待所有任务完成</p>
                <p>• 系统自动处理失败重试，无需手动干预</p>
                <p>• 停止录音后页面URL会包含UUID格式的音频ID，可分享或收藏</p>
                <p className="text-blue-400">• 每个片段都提供本地和远程播放按钮，便于调试</p>
                <p className="text-blue-400">• 本地音频缓存在浏览器中，可随时播放验证录音质量</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecordTestPageNew;